/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Money formatting helpers — single source of truth.
 *
 * Avoid inlining toLocaleString('en-SG', { ... }) elsewhere. If invoice
 * templates ever need "SGD " instead of "S$", or a different decimal
 * convention, change it here once.
 */

/** Format an SGD amount as "S$1,234.56". Two decimal places, comma thousands. */
export const formatSgd = (n: number): string =>
  `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Round a money amount to 2 decimal places (whole cents) by rounding on the
 * cent value. Single source of truth for money rounding across the app —
 * payment/surcharge math, PR/PO line totals, claims, cash-float, and receipts
 * all share this so cent-precision behaviour can never drift between surfaces
 * (docs/lessons.md Lesson #16: never reintroduce `toFixed(2)` where rounding mode
 * matters). The Cloud Functions bundle inlines this file
 * by `npm run sync:shared` — edit here, not there.
 */
export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** An SGD amount as whole cents. NaN stays NaN on purpose: every compare
 *  below then reads "not paid", which is how the `Math.round(x * 100)` checks
 *  this replaced failed — closed, never toward "settled". */
export const toCents = (sgd: number): number => Math.round(sgd * 100);

/**
 * THE definition of "paid in full" (backlog #139): creditable payments reach
 * the total in whole cents, with no slack — the rule the money Cloud Functions
 * use when they store FULLY_PAID. Every settlement surface calls this (the
 * customer panel, `createPaymentIntent`'s refusal, the schedule's Final row,
 * the payment email, the registers), so one cent short reads as unpaid
 * everywhere and the server accepts that cent. Pass creditable money only
 * (surcharge and quarantined entries excluded).
 *
 * Not for the deposit THRESHOLD, which keeps its one-cent `CENT_SLACK`
 * (depositGate.ts), nor for whether a progress request is met
 * (paymentRequest.ts) — those are different questions.
 */
export const isPaidInFull = (paid: number, total: number): boolean =>
  toCents(paid) >= toCents(total);

/** Cents still owed against `total`, floored at 0. Zero exactly when
 *  `isPaidInFull(paid, total)`. */
export const outstandingCents = (total: number, paid: number): number =>
  Math.max(0, toCents(total) - toCents(paid));
